import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const ACCESS_STORAGE_KEY = "nuvvy_local_screens_access_hash_v1";
const PASSWORD_HASH = "3c0790adf94018a47a42074e7dc765f049d3a4c5dd1f38541a732b4a811ef3e2";

const hashText = async (value) => {
  const encoded = new TextEncoder().encode(value);
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", encoded);
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

const ProtectedLocalPasswordRoute = ({ children }) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isAllowed, setIsAllowed] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const savedAccessHash = localStorage.getItem(ACCESS_STORAGE_KEY);
    setIsAllowed(savedAccessHash === PASSWORD_HASH);
    setIsChecking(false);
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!password.trim()) {
      setError("من فضلك أدخل كلمة المرور");
      return;
    }

    setIsSubmitting(true);

    try {
      const enteredPasswordHash = await hashText(password);

      if (enteredPasswordHash !== PASSWORD_HASH) {
        setError("كلمة المرور غير صحيحة");
        return;
      }

      localStorage.setItem(ACCESS_STORAGE_KEY, enteredPasswordHash);
      setIsAllowed(true);
      setPassword("");
    } catch (err) {
      console.error("Local password check failed:", err);
      setError("تعذر التحقق من كلمة المرور، حاول مرة أخرى");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isChecking) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isAllowed) {
    return children;
  }

  return (
    <Box
      dir="rtl"
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        py: 4,
        background: "linear-gradient(135deg, #f6f9ef 0%, #edf6f4 50%, #fff8ec 100%)",
      }}
    >
      <Paper
        component="form"
        onSubmit={handleSubmit}
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 420,
          p: { xs: 2.5, sm: 3 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 16px 45px rgba(24, 47, 35, 0.14)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              bgcolor: "success.light",
              color: "success.dark",
            }}
          >
            <LockOutlinedIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              صفحة محمية
            </Typography>
            <Typography variant="body2" color="text.secondary">
              أدخل كلمة المرور لفتح شاشة الطلبات والمشروبات
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          fullWidth
          label="كلمة المرور"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          disabled={isSubmitting}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"}
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          fullWidth
          type="submit"
          variant="contained"
          color="success"
          disabled={isSubmitting}
          sx={{ mt: 2.5, py: 1.1, fontWeight: 800 }}
        >
          {isSubmitting ? "جاري التحقق..." : "فتح الصفحة"}
        </Button>
      </Paper>
    </Box>
  );
};

export default ProtectedLocalPasswordRoute;
